// Utility functions for managing cookie consent
import type { PostHog } from 'posthog-js';
export const CONSENT_STORAGE_KEY = 'cookie_consent';
export const CONSENT_COOKIE_NAME = 'cookie_consent';

// Strict union type for consent values
export type ConsentStatus = 'accepted' | 'rejected' | 'undecided';

// Server-side cookie reading utility
export const getServerSideConsent = (cookieHeader: string | null): ConsentStatus => {
  if (!cookieHeader) {
    return 'undecided';
  }

  // Safe decode helper to handle malformed percent-encodings
  const safeDecode = (value: string): string => {
    try {
      return decodeURIComponent(value);
    } catch (error) {
      console.warn('Failed to decode cookie value, using raw value:', error);
      return value;
    }
  };

  const cookies = cookieHeader.split(';').reduce(
    (acc, cookie) => {
      const trimmedCookie = cookie.trim();
      if (!trimmedCookie) {
        return acc; // Skip empty or whitespace-only entries
      }

      const firstEqualsIndex = trimmedCookie.indexOf('=');
      if (firstEqualsIndex === -1) {
        return acc; // Skip entries without '=' (malformed cookies)
      }

      const key = trimmedCookie.substring(0, firstEqualsIndex).trim();
      const value = trimmedCookie.substring(firstEqualsIndex + 1).trim();

      if (key && value) {
        acc[key] = safeDecode(value);
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  const consent = cookies[CONSENT_COOKIE_NAME];
  if (!consent) {
    return 'undecided';
  }

  // Normalize the value to ensure it's one of our union members
  switch (consent) {
    case 'yes':
    case 'accepted':
      return 'accepted';
    case 'no':
    case 'rejected':
      return 'rejected';
    default:
      return 'undecided';
  }
};

export const getCookieConsent = (): ConsentStatus => {
  if (typeof window === 'undefined') {
    return 'undecided';
  }

  try {
    const consent = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!consent) {
      return 'undecided';
    }

    // Normalize the value to ensure it's one of our union members
    switch (consent) {
      case 'yes':
      case 'accepted':
        return 'accepted';
      case 'no':
      case 'rejected':
        return 'rejected';
      default:
        return 'undecided';
    }
  } catch (error) {
    // Handle SecurityError (private browsing) or QuotaExceededError
    console.warn('Failed to read consent from localStorage:', error);
    return 'undecided';
  }
};

export const setCookieConsent = (consent: ConsentStatus): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Map the new union type to the legacy storage format for backward compatibility
  const storageValue = consent === 'accepted' ? 'yes' : consent === 'rejected' ? 'no' : 'undecided';

  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, storageValue);
  } catch (error) {
    // Handle SecurityError (private browsing) or QuotaExceededError
    console.warn('Failed to write consent to localStorage:', error);
  }

  try {
    // Also set a cookie for server-side reading
    const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
    const secureFlag = isSecure ? '; Secure' : '';
    const cookieValue = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(storageValue)}; path=/; max-age=31536000; SameSite=Lax${secureFlag}`;
    document.cookie = cookieValue;
  } catch (cookieError) {
    // Handle HttpOnly cookie or other cookie write failures
    console.warn('Failed to write consent cookie:', cookieError);
  }
};

export const hasCookieConsent = (): boolean => {
  return getCookieConsent() === 'accepted';
};

export const isCookieConsentRejected = (): boolean => {
  return getCookieConsent() === 'rejected';
};

export const clearCookieConsent = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);

    // Also expire the server-visible cookie by setting it with a past expiration date
    const pastDate = new Date(0).toUTCString();
    const isSecure = window.location.protocol === 'https:';
    const secureFlag = isSecure ? '; Secure' : '';
    const cookieExpireValue = `${CONSENT_COOKIE_NAME}=; expires=${pastDate}; path=/; SameSite=Lax${secureFlag}`;

    try {
      document.cookie = cookieExpireValue;
    } catch (cookieError) {
      // Handle HttpOnly cookie or other cookie write failures
      console.warn('Failed to expire consent cookie:', cookieError);
    }
  } catch (error) {
    // Handle SecurityError (private browsing) or QuotaExceededError
    console.warn('Failed to clear consent from localStorage:', error);
  }
};

// Utility to handle Meta Pixel consent changes
export const handleMetaPixelConsent = (consent: ConsentStatus): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (consent === 'rejected') {
    // Call Facebook Pixel consent revoke API if available
    if (typeof window.fbq === 'function') {
      try {
        window.fbq('consent', 'revoke');
      } catch (error) {
        console.warn('Failed to revoke Facebook Pixel consent:', error);
      }
    }

    // Clear Meta Pixel cookies by setting them with past expiry
    const metaCookies = ['_fbp', '_fbc'];
    const pastDate = new Date(0).toUTCString();

    metaCookies.forEach((cookieName) => {
      try {
        // Generate apex domain variants for comprehensive cookie clearing
        const hostname = window.location.hostname;
        const hostnameParts = hostname.split('.');
        const isLocalhost = hostname === 'localhost';

        let apexDomain = '';
        if (!isLocalhost && hostnameParts.length > 2) {
          // Remove leftmost label for apex domain (e.g., www.example.com -> example.com)
          apexDomain = hostnameParts.slice(-2).join('.');
        }

        // Clear cookie with various domain/path combinations to ensure complete removal
        const cookieOptions = [
          `${cookieName}=; expires=${pastDate}; path=/`,
          `${cookieName}=; expires=${pastDate}; path=/; domain=${hostname}`,
          `${cookieName}=; expires=${pastDate}; path=/; domain=.${hostname}`,
        ];

        // Add apex domain variants if applicable
        if (apexDomain) {
          cookieOptions.push(
            `${cookieName}=; expires=${pastDate}; path=/; domain=${apexDomain}`,
            `${cookieName}=; expires=${pastDate}; path=/; domain=.${apexDomain}`,
          );
        }

        cookieOptions.forEach((option) => {
          document.cookie = option;
        });
      } catch (error) {
        console.warn(`Failed to clear Meta cookie ${cookieName}:`, error);
      }
    });

    // Reload the page to properly disable Meta Pixel
    // since it's loaded in the head and can't be easily unloaded
    window.location.reload();
  }
  // If consent is 'accepted', Meta Pixel will be initialized by the provider
};

// Utility to handle Google Analytics consent changes
export const handleGoogleAnalyticsConsent = (consent: ConsentStatus): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (consent === 'rejected') {
    // Clear Google Analytics cookies
    const gaCookies = ['_ga', '_gid', '_gat'];
    // Only add the specific _gat_gtag_ cookie if measurement ID is valid
    const measurementId = (process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || '').trim();
    if (measurementId) {
      gaCookies.push(`_gat_gtag_${measurementId}`);
    }
    const pastDate = new Date(0).toUTCString();

    gaCookies.forEach((cookieName) => {
      try {
        const hostname = window.location.hostname;
        const hostnameParts = hostname.split('.');
        const isLocalhost = hostname === 'localhost';

        let apexDomain = '';
        if (!isLocalhost && hostnameParts.length > 2) {
          apexDomain = hostnameParts.slice(-2).join('.');
        }

        const cookieOptions = [
          `${cookieName}=; expires=${pastDate}; path=/`,
          `${cookieName}=; expires=${pastDate}; path=/; domain=${hostname}`,
          `${cookieName}=; expires=${pastDate}; path=/; domain=.${hostname}`,
        ];

        if (apexDomain) {
          cookieOptions.push(
            `${cookieName}=; expires=${pastDate}; path=/; domain=${apexDomain}`,
            `${cookieName}=; expires=${pastDate}; path=/; domain=.${apexDomain}`,
          );
        }

        cookieOptions.forEach((option) => {
          document.cookie = option;
        });
      } catch (error) {
        console.warn(`Failed to clear Google Analytics cookie ${cookieName}:`, error);
      }
    });
  }
};

// PostHog utility functions
declare global {
  interface Window {
    posthog?: PostHog;
  }
}

export const isPostHogReady = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const isPostHogLoaded = typeof window.posthog === 'object' && window.posthog !== null;

  return isPostHogLoaded;
};
